/**
 * Readable labels for the answers the recruiter stores as short codes.
 *
 * The codes are the `value` of each quick reply in
 * `taaly_backend/src/modules/jobs/recruiter/utils/recruiter-script.ts`, and the
 * English is what the learner tapped, from `intl_en.arb` in `taaly_mobile`.
 * Keyed per field on purpose: `geen` is "No school" as an education answer and
 * "Walking" as a transport one.
 *
 * A code this map does not know is shown as it is stored, not hidden — a new
 * answer added to the script should read as unfamiliar, never as missing.
 */

const EDUCATION: Record<string, string> = {
  geen: "No school",
  basisonderwijs: "Primary school",
  voortgezet_onderwijs: "Secondary school",
  mbo: "MBO (vocational)",
  bachelor_hbo: "HBO / University — bachelor",
  master_hbo: "University — master",
  phd: "PhD",
  anders: "Something else",
};

const RESIDENCY: Record<string, string> = {
  statushouder: "Status holder",
  asielprocedure_kort: "Asylum procedure, less than 6 months in NL",
  asielprocedure_lang: "Asylum procedure, more than 6 months",
  tijdelijke_bescherming: "Temporary protection (for example Ukraine)",
  nederlandse_nationaliteit: "Dutch nationality",
};

const TRANSPORT: Record<string, string> = {
  geen: "Walking",
  fiets: "Bicycle",
  ov: "Public transport",
  auto: "Car",
};

const TRAVEL_DISTANCE: Record<string, string> = {
  eigen_gemeente: "Only in their municipality",
  tot_30_min: "Up to 30 minutes",
  verder: "Further is okay",
};

const START_WHEN: Record<string, string> = {
  nu: "Now",
  een_maand: "In a month",
  later: "Later",
};

const lookup = (map: Record<string, string>, code?: string): string | null =>
  code ? map[code] ?? code : null;

export const educationLabel = (code?: string) => lookup(EDUCATION, code);
export const transportLabel = (code?: string) => lookup(TRANSPORT, code);
export const travelDistanceLabel = (code?: string) => lookup(TRAVEL_DISTANCE, code);
export const startWhenLabel = (code?: string) => lookup(START_WHEN, code);

/**
 * Declining is a supported answer, and it must read as one: a blank here would
 * look like a question nobody asked.
 */
export const residencyLabel = (status?: string, declined?: boolean): string | null => {
  if (declined) return "Declined to say";
  return lookup(RESIDENCY, status);
};

/** `hasBsn` is absent when the learner answered "I do not know". */
export const bsnLabel = (hasBsn?: boolean): string => {
  if (hasBsn === true) return "Yes";
  if (hasBsn === false) return "No";
  return "Does not know";
};

export const yesNoLabel = (value?: boolean): string | null => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return null;
};

/** Timestamps arrive as ISO strings on rows; an empty string means none. */
export const formatDate = (iso: string): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

export const formatDateTime = (iso: string): string =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const CV_TYPE_NAMES: Record<string, string> = {
  "application/pdf": "PDF",
  "application/msword": "Word document",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word document",
  "text/plain": "Text file",
  "image/jpeg": "Photo (JPEG)",
  "image/png": "Image (PNG)",
  "image/heic": "Photo (HEIC)",
  "image/heif": "Photo (HEIF)",
};

/**
 * What the CV is, named before anyone opens it.
 *
 * The recruiter accepts a photo of a paper CV on purpose — for this audience it
 * is often the only CV there is — so an image here is not a fault. It is still
 * something the reader has to know in advance, because a photo may be worth a
 * message back to the learner where a PDF never is.
 *
 * An unrecognised type is shown as stored rather than hidden. The bucket already
 * holds CVs saved as `application/octet-stream`, and "unknown type" is the
 * honest label for a file the browser will not know what to do with either.
 */
export const cvTypeLabel = (contentType: string): string =>
  CV_TYPE_NAMES[contentType] ?? (contentType ? `Unknown type (${contentType})` : "Unknown type");

/** Images are the case worth flagging; see [cvTypeLabel]. */
export const cvIsImage = (contentType: string): boolean => contentType.startsWith("image/");

export const formatFileSize = (bytes: number): string => {
  if (!bytes) return "empty";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
